import React, { useState } from 'react';
import { DollarSign, Users, Gift, Coffee, TrendingUp, Building2, Share2, CreditCard, BarChart3, Zap, ArrowLeft, ArrowRight, ChevronDown } from 'lucide-react';
import AdminNav from './AdminNav';

export default function MonetizationFlowDiagram() {
  const [selectedFlow, setSelectedFlow] = useState('overview');
  const [expandedNodes, setExpandedNodes] = useState<Set<string>>(new Set(['revenue']));

  const toggleNode = (nodeId: string) => {
    const newExpanded = new Set(expandedNodes);
    if (newExpanded.has(nodeId)) {
      newExpanded.delete(nodeId);
    } else {
      newExpanded.add(nodeId);
    }
    setExpandedNodes(newExpanded);
  };

  return (
    <div className="min-h-screen bg-slate-900">
      <AdminNav
        title="Monetization Flow"
        description="View monetization flow diagram"
      />
      <div className="bg-gradient-to-br from-amber-50 via-orange-50 to-rose-50 p-8">
        <div className="max-w-7xl mx-auto">
          {/* Header */}
          <div className="text-center mb-12">
            <div className="flex items-center justify-center gap-3 mb-4">
              <Coffee className="w-12 h-12 text-amber-600" />
              <h1 className="text-5xl font-bold text-amber-900">
                Birthday Coffee Revenue Engine
              </h1>
            </div>
            <p className="text-xl text-amber-700">Working Backwards from $1M ARR</p>
          </div>

        {/* Flow Selector */}
        <div className="flex justify-center gap-4 mb-12">
          <button
            onClick={() => setSelectedFlow('overview')}
            className={`px-6 py-3 rounded-full font-semibold transition-all ${
              selectedFlow === 'overview'
                ? 'bg-amber-600 text-white shadow-lg shadow-amber-600/20'
                : 'bg-white text-amber-700 hover:bg-amber-100 border-2 border-amber-200'
            }`}
          >
            Revenue Overview
          </button>
          <button
            onClick={() => setSelectedFlow('transaction')}
            className={`px-6 py-3 rounded-full font-semibold transition-all ${
              selectedFlow === 'transaction'
                ? 'bg-orange-600 text-white shadow-lg shadow-orange-600/20'
                : 'bg-white text-amber-700 hover:bg-amber-100 border-2 border-amber-200'
            }`}
          >
            Transaction Flow
          </button>
          <button
            onClick={() => setSelectedFlow('corporate')}
            className={`px-6 py-3 rounded-full font-semibold transition-all ${
              selectedFlow === 'corporate'
                ? 'bg-rose-600 text-white shadow-lg shadow-rose-600/20'
                : 'bg-white text-amber-700 hover:bg-amber-100 border-2 border-amber-200'
            }`}
          >
            Corporate Flow
          </button>
        </div>

        {/* Main Flow Diagram */}
        {selectedFlow === 'overview' && (
          <div className="space-y-8">
            {/* Level 1: Revenue Goal */}
            <div className="flex justify-center">
              <div
                className="bg-gradient-to-r from-amber-600 to-orange-600 p-8 rounded-2xl shadow-2xl cursor-pointer transform hover:scale-105 transition-all border-4 border-white"
                onClick={() => toggleNode('revenue')}
              >
                <div className="flex items-center gap-4">
                  <DollarSign className="w-12 h-12 text-white" />
                  <div>
                    <h2 className="text-3xl font-bold text-white">$1M Annual Revenue</h2>
                    <p className="text-amber-100">Target: $83,333/month</p>
                  </div>
                  <ChevronDown className={`w-6 h-6 text-white transition-transform ${expandedNodes.has('revenue') ? 'rotate-180' : ''}`} />
                </div>
              </div>
            </div>

            {/* Arrow */}
            {expandedNodes.has('revenue') && (
              <>
                <div className="flex justify-center">
                  <ArrowLeft className="w-8 h-8 text-amber-400 animate-pulse" />
                </div>

                {/* Level 2: Revenue Streams */}
                <div className="grid grid-cols-4 gap-6">
                  {/* Transaction Fees */}
                  <div className="bg-white p-6 rounded-xl border-2 border-amber-300 shadow-xl">
                    <div className="flex items-center gap-3 mb-4">
                      <CreditCard className="w-8 h-8 text-amber-600" />
                      <h3 className="text-xl font-bold text-amber-900">Transaction Fees</h3>
                    </div>
                    <p className="text-amber-700 mb-3">15% on all gifts</p>
                    <div className="bg-amber-50 p-3 rounded-lg border border-amber-200">
                      <p className="text-amber-600 font-mono font-bold">$40,000/mo</p>
                      <p className="text-amber-600 text-sm">~17,800 transactions</p>
                      <p className="text-amber-500 text-xs">Avg gift: $15</p>
                    </div>
                  </div>

                  {/* Corporate Subscriptions */}
                  <div className="bg-white p-6 rounded-xl border-2 border-rose-300 shadow-xl">
                    <div className="flex items-center gap-3 mb-4">
                      <Building2 className="w-8 h-8 text-rose-600" />
                      <h3 className="text-xl font-bold text-amber-900">Corporate Plans</h3>
                    </div>
                    <p className="text-amber-700 mb-3">B2B Subscriptions</p>
                    <div className="bg-rose-50 p-3 rounded-lg border border-rose-200">
                      <p className="text-rose-600 font-mono font-bold">$30,000/mo</p>
                      <p className="text-rose-600 text-sm">60 companies</p>
                      <p className="text-rose-500 text-xs">Avg: $500/mo</p>
                    </div>
                  </div>

                  {/* Affiliate Revenue */}
                  <div className="bg-white p-6 rounded-xl border-2 border-orange-300 shadow-xl">
                    <div className="flex items-center gap-3 mb-4">
                      <Coffee className="w-8 h-8 text-orange-600" />
                      <h3 className="text-xl font-bold text-amber-900">Partner Revenue</h3>
                    </div>
                    <p className="text-amber-700 mb-3">Coffee shop partners</p>
                    <div className="bg-orange-50 p-3 rounded-lg border border-orange-200">
                      <p className="text-orange-600 font-mono font-bold">$10,000/mo</p>
                      <p className="text-orange-600 text-sm">50 partners</p>
                      <p className="text-orange-500 text-xs">30% commission</p>
                    </div>
                  </div>

                  {/* Premium Features */}
                  <div className="bg-white p-6 rounded-xl border-2 border-yellow-400 shadow-xl">
                    <div className="flex items-center gap-3 mb-4">
                      <Zap className="w-8 h-8 text-yellow-600" />
                      <h3 className="text-xl font-bold text-amber-900">Premium Features</h3>
                    </div>
                    <p className="text-amber-700 mb-3">Add-ons & upgrades</p>
                    <div className="bg-yellow-50 p-3 rounded-lg border border-yellow-200">
                      <p className="text-yellow-600 font-mono font-bold">$3,333/mo</p>
                      <p className="text-yellow-600 text-sm">~1,100 upgrades</p>
                      <p className="text-yellow-500 text-xs">Avg: $3/upgrade</p>
                    </div>
                  </div>
                </div>

                <div className="flex justify-center">
                  <ArrowLeft className="w-8 h-8 text-amber-400 animate-pulse" />
                </div>

                {/* Level 3: Required Infrastructure */}
                <div className="grid grid-cols-3 gap-6">
                  {/* User Acquisition */}
                  <div className="bg-white p-6 rounded-xl shadow-lg border-2 border-amber-200">
                    <div className="flex items-center gap-3 mb-4">
                      <Users className="w-8 h-8 text-amber-600" />
                      <h3 className="text-lg font-bold text-amber-900">User Acquisition</h3>
                    </div>
                    <div className="space-y-2">
                      <div className="bg-amber-50 p-2 rounded-lg border border-amber-200">
                        <p className="text-amber-700 text-sm font-semibold">Target: 50,000 MAU</p>
                      </div>
                      <div className="bg-orange-50 p-2 rounded-lg">
                        <p className="text-amber-600 text-sm">• SEO & Content</p>
                        <p className="text-amber-600 text-sm">• Social Media Ads</p>
                        <p className="text-amber-600 text-sm">• Referral Program</p>
                        <p className="text-amber-600 text-sm">• Influencer Partnerships</p>
                      </div>
                    </div>
                  </div>

                  {/* Technology Stack */}
                  <div className="bg-white p-6 rounded-xl shadow-lg border-2 border-amber-200">
                    <div className="flex items-center gap-3 mb-4">
                      <BarChart3 className="w-8 h-8 text-orange-600" />
                      <h3 className="text-lg font-bold text-amber-900">Tech Infrastructure</h3>
                    </div>
                    <div className="space-y-2">
                      <div className="bg-orange-50 p-2 rounded-lg border border-orange-200">
                        <p className="text-orange-700 text-sm font-semibold">Core Systems</p>
                      </div>
                      <div className="bg-amber-50 p-2 rounded-lg">
                        <p className="text-amber-600 text-sm">• Stripe Payments</p>
                        <p className="text-amber-600 text-sm">• CloudFlare Workers</p>
                        <p className="text-amber-600 text-sm">• Resend Email</p>
                        <p className="text-amber-600 text-sm">• Analytics (Mixpanel)</p>
                      </div>
                    </div>
                  </div>

                  {/* Operations */}
                  <div className="bg-white p-6 rounded-xl shadow-lg border-2 border-amber-200">
                    <div className="flex items-center gap-3 mb-4">
                      <Share2 className="w-8 h-8 text-rose-600" />
                      <h3 className="text-lg font-bold text-amber-900">Operations</h3>
                    </div>
                    <div className="space-y-2">
                      <div className="bg-rose-50 p-2 rounded-lg border border-rose-200">
                        <p className="text-rose-700 text-sm font-semibold">Support & Growth</p>
                      </div>
                      <div className="bg-amber-50 p-2 rounded-lg">
                        <p className="text-amber-600 text-sm">• Customer Support</p>
                        <p className="text-amber-600 text-sm">• Partner Onboarding</p>
                        <p className="text-amber-600 text-sm">• Fraud Prevention</p>
                        <p className="text-amber-600 text-sm">• Compliance</p>
                      </div>
                    </div>
                  </div>
                </div>
              </>
            )}
          </div>
        )}

        {/* Transaction Flow */}
        {selectedFlow === 'transaction' && (
          <div className="space-y-8">
            <div className="bg-white rounded-2xl p-8 border-4 border-amber-200 shadow-xl">
              <h2 className="text-2xl font-bold text-amber-900 mb-8 text-center">Individual Transaction Revenue Flow</h2>

              {/* Flow Steps */}
              <div className="flex items-center justify-between">
                {/* Revenue Collected */}
                <div className="bg-gradient-to-br from-amber-500 to-orange-600 p-4 rounded-xl text-center border-2 border-white">
                  <DollarSign className="w-10 h-10 text-white mx-auto mb-2" />
                  <p className="text-white font-bold">$2.25 Revenue</p>
                  <p className="text-amber-100 text-xs">Per $15 gift</p>
                </div>

                <ArrowLeft className="w-6 h-6 text-amber-400" />

                {/* Service Fee */}
                <div className="bg-amber-50 p-4 rounded-xl text-center border-2 border-amber-300">
                  <p className="text-amber-800 font-semibold">15% Service Fee</p>
                  <p className="text-amber-600 text-sm">Applied at checkout</p>
                </div>

                <ArrowLeft className="w-6 h-6 text-amber-400" />

                {/* Transaction */}
                <div className="bg-orange-50 p-4 rounded-xl text-center border-2 border-orange-300">
                  <Gift className="w-8 h-8 text-orange-600 mx-auto mb-2" />
                  <p className="text-amber-800 font-semibold">Gift Sent</p>
                  <p className="text-amber-600 text-sm">$15 coffee gift</p>
                </div>

                <ArrowLeft className="w-6 h-6 text-amber-400" />

                {/* User Action */}
                <div className="bg-rose-50 p-4 rounded-xl text-center border-2 border-rose-300">
                  <Users className="w-8 h-8 text-rose-600 mx-auto mb-2" />
                  <p className="text-amber-800 font-semibold">User Initiates</p>
                  <p className="text-amber-600 text-sm">Birthday reminder</p>
                </div>
              </div>

              {/* Additional Revenue Opportunities */}
              <div className="mt-12 grid grid-cols-4 gap-4">
                <div className="bg-amber-50 p-4 rounded-lg border border-amber-200">
                  <p className="text-amber-700 font-semibold mb-2">+ Premium Card</p>
                  <p className="text-amber-600 text-sm">$3 upgrade</p>
                  <p className="text-amber-500 text-xs">30% attach rate</p>
                </div>
                <div className="bg-orange-50 p-4 rounded-lg border border-orange-200">
                  <p className="text-orange-700 font-semibold mb-2">+ Video Message</p>
                  <p className="text-orange-600 text-sm">$5 upgrade</p>
                  <p className="text-orange-500 text-xs">15% attach rate</p>
                </div>
                <div className="bg-rose-50 p-4 rounded-lg border border-rose-200">
                  <p className="text-rose-700 font-semibold mb-2">+ Express Delivery</p>
                  <p className="text-rose-600 text-sm">$5 upgrade</p>
                  <p className="text-rose-500 text-xs">20% attach rate</p>
                </div>
                <div className="bg-yellow-50 p-4 rounded-lg border border-yellow-200">
                  <p className="text-yellow-700 font-semibold mb-2">+ Schedule Future</p>
                  <p className="text-yellow-600 text-sm">$2 upgrade</p>
                  <p className="text-yellow-500 text-xs">25% attach rate</p>
                </div>
              </div>

              {/* Metrics */}
              <div className="mt-8 bg-amber-50 p-6 rounded-xl border-2 border-amber-300">
                <h3 className="text-lg font-bold text-amber-900 mb-4">Transaction Economics</h3>
                <div className="grid grid-cols-3 gap-4">
                  <div>
                    <p className="text-amber-600 text-sm">Average Order Value</p>
                    <p className="text-amber-700 text-2xl font-bold">$18.75</p>
                  </div>
                  <div>
                    <p className="text-amber-600 text-sm">Revenue per Transaction</p>
                    <p className="text-amber-700 text-2xl font-bold">$3.85</p>
                  </div>
                  <div>
                    <p className="text-amber-600 text-sm">Monthly Transactions Needed</p>
                    <p className="text-amber-700 text-2xl font-bold">10,390</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Corporate Flow */}
        {selectedFlow === 'corporate' && (
          <div className="space-y-8">
            <div className="bg-white rounded-2xl p-8 border-4 border-amber-200 shadow-xl">
              <h2 className="text-2xl font-bold text-amber-900 mb-8 text-center">Corporate Account Revenue Flow</h2>

              {/* Corporate Tiers */}
              <div className="grid grid-cols-3 gap-6 mb-8">
                <div className="bg-gradient-to-br from-amber-500 to-amber-600 p-6 rounded-xl border-2 border-white">
                  <h3 className="text-xl font-bold text-white mb-4">Starter</h3>
                  <p className="text-amber-100 text-3xl font-bold mb-2">$299/mo</p>
                  <div className="space-y-2">
                    <p className="text-amber-50 text-sm">✓ Up to 50 employees</p>
                    <p className="text-amber-50 text-sm">✓ Basic automation</p>
                    <p className="text-amber-50 text-sm">✓ Standard cards</p>
                    <p className="text-amber-50 text-sm">✓ Monthly reporting</p>
                  </div>
                </div>

                <div className="bg-gradient-to-br from-orange-500 to-orange-600 p-6 rounded-xl border-2 border-white">
                  <h3 className="text-xl font-bold text-white mb-4">Professional</h3>
                  <p className="text-orange-100 text-3xl font-bold mb-2">$599/mo</p>
                  <div className="space-y-2">
                    <p className="text-orange-50 text-sm">✓ Up to 200 employees</p>
                    <p className="text-orange-50 text-sm">✓ Custom branding</p>
                    <p className="text-orange-50 text-sm">✓ Premium cards</p>
                    <p className="text-orange-50 text-sm">✓ API access</p>
                  </div>
                </div>

                <div className="bg-gradient-to-br from-rose-500 to-rose-600 p-6 rounded-xl border-2 border-white">
                  <h3 className="text-xl font-bold text-white mb-4">Enterprise</h3>
                  <p className="text-rose-100 text-3xl font-bold mb-2">$999/mo</p>
                  <div className="space-y-2">
                    <p className="text-rose-50 text-sm">✓ Unlimited employees</p>
                    <p className="text-rose-50 text-sm">✓ White label</p>
                    <p className="text-rose-50 text-sm">✓ Dedicated support</p>
                    <p className="text-rose-50 text-sm">✓ Custom integrations</p>
                  </div>
                </div>
              </div>

              {/* Revenue Breakdown */}
              <div className="bg-amber-50 p-6 rounded-xl border-2 border-amber-300">
                <h3 className="text-lg font-bold text-amber-900 mb-4">Corporate Revenue Model</h3>
                <div className="space-y-4">
                  <div className="flex justify-between items-center">
                    <div>
                      <p className="text-amber-700">20 Starter Accounts</p>
                      <p className="text-amber-600 text-sm">Small businesses</p>
                    </div>
                    <p className="text-amber-700 font-mono font-bold">$5,980/mo</p>
                  </div>
                  <div className="flex justify-between items-center">
                    <div>
                      <p className="text-amber-700">30 Professional Accounts</p>
                      <p className="text-amber-600 text-sm">Mid-market companies</p>
                    </div>
                    <p className="text-amber-700 font-mono font-bold">$17,970/mo</p>
                  </div>
                  <div className="flex justify-between items-center">
                    <div>
                      <p className="text-amber-700">10 Enterprise Accounts</p>
                      <p className="text-amber-600 text-sm">Large corporations</p>
                    </div>
                    <p className="text-amber-700 font-mono font-bold">$9,990/mo</p>
                  </div>
                  <div className="border-t border-amber-400 pt-4 flex justify-between items-center">
                    <p className="text-amber-900 font-bold">Total Corporate Revenue</p>
                    <p className="text-amber-700 font-mono text-xl font-bold">$33,940/mo</p>
                  </div>
                </div>
              </div>

              {/* Acquisition Strategy */}
              <div className="mt-8 grid grid-cols-2 gap-6">
                <div className="bg-orange-50 p-6 rounded-xl border-2 border-orange-300">
                  <h3 className="text-lg font-bold text-amber-900 mb-4">Acquisition Channels</h3>
                  <div className="space-y-2">
                    <div className="flex justify-between">
                      <p className="text-amber-700">LinkedIn Outreach</p>
                      <p className="text-amber-600">35%</p>
                    </div>
                    <div className="flex justify-between">
                      <p className="text-amber-700">Partner Referrals</p>
                      <p className="text-amber-600">25%</p>
                    </div>
                    <div className="flex justify-between">
                      <p className="text-amber-700">Content Marketing</p>
                      <p className="text-amber-600">20%</p>
                    </div>
                    <div className="flex justify-between">
                      <p className="text-amber-700">Direct Sales</p>
                      <p className="text-amber-600">20%</p>
                    </div>
                  </div>
                </div>

                <div className="bg-rose-50 p-6 rounded-xl border-2 border-rose-300">
                  <h3 className="text-lg font-bold text-amber-900 mb-4">Value Proposition</h3>
                  <div className="space-y-2">
                    <p className="text-amber-700 text-sm">• Improve employee satisfaction</p>
                    <p className="text-amber-700 text-sm">• Automate HR celebrations</p>
                    <p className="text-amber-700 text-sm">• Tax-deductible expense</p>
                    <p className="text-amber-700 text-sm">• Build company culture</p>
                    <p className="text-amber-700 text-sm">• Detailed analytics & reporting</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Key Metrics Dashboard */}
        <div className="mt-12 bg-white rounded-2xl p-8 border-4 border-amber-200 shadow-xl">
          <h2 className="text-2xl font-bold text-amber-900 mb-6">Key Success Metrics</h2>
          <div className="grid grid-cols-4 gap-6">
            <div className="bg-amber-50 p-4 rounded-xl border-2 border-amber-300">
              <p className="text-amber-600 text-sm mb-2">Monthly Active Users</p>
              <p className="text-amber-700 text-3xl font-bold">50,000</p>
              <p className="text-amber-500 text-xs">↑ 15% MoM</p>
            </div>
            <div className="bg-orange-50 p-4 rounded-xl border-2 border-orange-300">
              <p className="text-orange-600 text-sm mb-2">Conversion Rate</p>
              <p className="text-orange-700 text-3xl font-bold">3.5%</p>
              <p className="text-orange-500 text-xs">Industry avg: 2.1%</p>
            </div>
            <div className="bg-rose-50 p-4 rounded-xl border-2 border-rose-300">
              <p className="text-rose-600 text-sm mb-2">Customer LTV</p>
              <p className="text-rose-700 text-3xl font-bold">$145</p>
              <p className="text-rose-500 text-xs">6-month retention</p>
            </div>
            <div className="bg-yellow-50 p-4 rounded-xl border-2 border-yellow-400">
              <p className="text-yellow-700 text-sm mb-2">CAC Payback</p>
              <p className="text-yellow-700 text-3xl font-bold">2.3 mo</p>
              <p className="text-yellow-600 text-xs">Target: &lt; 3 months</p>
            </div>
          </div>
        </div>

        {/* Implementation Timeline */}
        <div className="mt-12 bg-white rounded-2xl p-8 border-4 border-amber-200 shadow-xl">
          <h2 className="text-2xl font-bold text-amber-900 mb-6">Implementation Roadmap</h2>
          <div className="space-y-4">
            <div className="flex items-center gap-4">
              <div className="bg-amber-600 text-white px-4 py-2 rounded-full font-bold">Phase 1</div>
              <div className="flex-1 bg-amber-50 p-4 rounded-lg border-2 border-amber-300">
                <p className="text-amber-800 font-semibold">Core Transaction System (Week 1-2)</p>
                <p className="text-amber-600 text-sm">Stripe integration, basic fee collection, transaction tracking</p>
              </div>
            </div>
            <div className="flex items-center gap-4">
              <div className="bg-orange-600 text-white px-4 py-2 rounded-full font-bold">Phase 2</div>
              <div className="flex-1 bg-orange-50 p-4 rounded-lg border-2 border-orange-300">
                <p className="text-amber-800 font-semibold">Admin Dashboard (Week 3-4)</p>
                <p className="text-amber-600 text-sm">Revenue metrics, transaction management, user analytics</p>
              </div>
            </div>
            <div className="flex items-center gap-4">
              <div className="bg-rose-600 text-white px-4 py-2 rounded-full font-bold">Phase 3</div>
              <div className="flex-1 bg-rose-50 p-4 rounded-lg border-2 border-rose-300">
                <p className="text-amber-800 font-semibold">Corporate Features (Week 5-6)</p>
                <p className="text-amber-600 text-sm">B2B accounts, bulk purchasing, custom branding</p>
              </div>
            </div>
            <div className="flex items-center gap-4">
              <div className="bg-yellow-600 text-white px-4 py-2 rounded-full font-bold">Phase 4</div>
              <div className="flex-1 bg-yellow-50 p-4 rounded-lg border-2 border-yellow-400">
                <p className="text-amber-800 font-semibold">Growth & Optimization (Week 7-8)</p>
                <p className="text-amber-600 text-sm">Partner system, premium features, marketing automation</p>
              </div>
            </div>
          </div>
          </div>
        </div>
      </div>
    </div>
  );
}